<template>
  <div 
    class="flex flex-col h-full bg-surface"
    :style="customStyles"
  >
    <template v-if="loginRequired && !authenticated">
      <LoginForm @login="handleLogin" />
    </template>
    <template v-else-if="!conversationInitialized">
      <VisitorForm 
        :widget-id="props.widgetId" 
        @submit="handleVisitorSubmit" 
      />
    </template>
    <template v-else>
      <ChatHeader 
        :title="widgetTitle"
        :avatar-url="widgetAvatarUrl"
        @close="minimizeChat"
      />
      <ChatMessages 
        :messages="messageStore.messages" 
        :loading="loading" 
        :user-id="userId"
        :bot-is-typing="botIsTyping"
      />
      <ChatInput 
        :disabled="inputDisabled" 
        :conversation-id="sessionStore.conversationId"
        @send="sendMessage"
        @upload="uploadFile"
        @typing="handleUserTyping"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watchEffect } from 'vue';
import { useMessageStore } from '@/store/messages';
import { useSessionStore } from '@/store/session';
import { useChatConfigStore } from '@/store/config';
import { socketService } from '@/services/socket.service';
import { generateUUID } from '@/utils/uuid';
// @ts-ignore
import axios from 'axios';

import ChatHeader from '@/components/ChatHeader.vue';
import ChatMessages from '@/components/ChatMessages.vue';
import ChatInput from '@/components/ChatInput.vue';
import LoginForm from '@/components/LoginForm.vue';
import VisitorForm from '@/components/VisitorForm.vue';
import { config } from './utils/config';

// Props from widget mount options
const props = defineProps<{
  widgetId: string;
  apiUrl: string;
  theme?: {
    primaryColor?: string;
    accentColor?: string;
    backgroundColor?: string;
    bubbleColor?: string;
  };
}>();

// Store instances
const messageStore = useMessageStore();
const sessionStore = useSessionStore();
const configStore = useChatConfigStore();

// Local state
const loading = ref(false);
const inputDisabled = ref(false);
const authenticated = ref(false);
const conversationInitialized = ref(false);
const userId = ref('');
const loginRequired = ref(false);
const widgetTitle = ref('Orbitar Assistant');
const widgetAvatarUrl = ref('');
const botIsTyping = ref(false);

// Computed styles based on theme
const customStyles = computed(() => ({
  '--color-primary': props.theme?.primaryColor || '#5C66BC',
  '--color-accent': props.theme?.accentColor || '#44DBAC',
  '--color-background': props.theme?.backgroundColor || '#4DB5AC',
  '--color-bubble': props.theme?.bubbleColor || '#F7F9FC',
}));

// Initialize new session with visitor info
const initChatWithVisitorInfo = async (visitorInfo: any) => {
  try {
    loading.value = true;
    
    // API call to initiate chat on the backend
    const response = await axios.post(`${config.apiUrl}/api/webchat/initiate`, {
      widgetId: props.widgetId,
      visitorInfo: {
        ...visitorInfo,
        // Make sure we're passing a proper UUID as the customer identifier if needed
        // The backend will use this for customer identification
        visitorId: visitorInfo.visitorId
      }
    });
    
    // Store the conversation ID in session store
    sessionStore.conversationId = response.data.conversationId;
    userId.value = visitorInfo.visitorId || sessionStore.userId;
    
    // Connect to socket and join the conversation
    socketService.connect();
    await socketService.joinConversation(response.data.conversationId);
    
    // Set conversation as initialized
    conversationInitialized.value = true;
  } catch (error) {
    console.error('[Orbitar Chat] Failed to create conversation:', error);
    messageStore.addSystemMessage('Failed to start conversation. Please try again.');
  } finally {
    loading.value = false;
  }
};

// Initialize session with existing conversationId
const initExistingSession = async () => {
  try {
    // Start a new conversation or continue existing one
    await sessionStore.initSession(props.widgetId);
    userId.value = sessionStore.userId;
    
    if (!sessionStore.conversationId) {
      // We need to have the user fill out the form for a new conversation
      return;
    }
    
    // Connect to socket and join the conversation
    socketService.connect();
    await socketService.joinConversation(sessionStore.conversationId);
    
    // Mark conversation as initialized
    conversationInitialized.value = true;
  } catch (error) {
    console.error('[Orbitar Chat] Failed to load existing session:', error);
  }
};

// Create new conversation with existing widget ID
const createNewConversation = async () => {
  try {
    loading.value = true;
    
    // Generate a UUID for this visitor
    const visitorId = generateUUID();
    userId.value = visitorId;
    
    // Connect to socket and use join_conversation with widgetId and visitorId
    socketService.connect();
    const conversationId = await socketService.joinConversation(
      null, 
      props.widgetId, 
      { visitorId }
    );
    
    // Store conversation ID in session
    if (conversationId) {
      sessionStore.conversationId = conversationId;
      
      // Save to local storage for persistence
      localStorage.setItem(`orbitar_conversation_${props.widgetId}`, conversationId);
      localStorage.setItem(`orbitar_user_${props.widgetId}`, visitorId);
      
      // Mark as initialized
      conversationInitialized.value = true;
    }
  } catch (error) {
    console.error('[Orbitar Chat] Failed to create new conversation:', error);
    messageStore.addSystemMessage('Failed to connect. Please try again.');
  } finally {
    loading.value = false;
  }
};

// Handle sending messages
const sendMessage = async (content: string) => {
  if (!sessionStore.conversationId || !content.trim()) return;

  try {
    const tempId = `temp-${Date.now()}`;
    messageStore.addMessage({
      id: tempId,
      content,
      senderType: 'user',
      timestamp: Date.now(),
      status: 'sending',
      type: 'text'
    });

    const messageId = await socketService.sendMessage(
      sessionStore.conversationId,
      content
    );

    messageStore.updateMessageStatus(tempId, 'sent');
  } catch (error) {
    console.error('[Orbitar Chat] Failed to send message:', error);
    messageStore.addSystemMessage('Failed to send message. Please try again.');
  }
};

// Handle file uploads
const uploadFile = async (file: File) => {
  if (!sessionStore.conversationId) return;

  try {
    loading.value = true;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('conversationId', sessionStore.conversationId);
    formData.append('widgetId', props.widgetId);

    // Add upload logic here
    // const response = await uploadApi.uploadFile(formData);
    
    messageStore.addMessage({
      id: `file-${Date.now()}`,
      content: file.name,
      senderType: 'user',
      timestamp: Date.now(),
      status: 'sent',
      type: 'file',
      metadata: {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        // url: response.url
      }
    });
  } catch (error) {
    console.error('[Orbitar Chat] Failed to upload file:', error);
    messageStore.addSystemMessage('Failed to upload file. Please try again.');
  } finally {
    loading.value = false;
  }
};

// Handle user typing status
const handleUserTyping = (isTyping: boolean) => {
  if (sessionStore.conversationId) {
    socketService.sendTypingStatus(sessionStore.conversationId, isTyping);
  }
};

// Handle external message sending
const handleExternalSendMessage = (event: CustomEvent) => {
  if (event.detail?.content) {
    sendMessage(event.detail.content);
  }
};

// Minimize chat window
const minimizeChat = () => {
  window.parent.postMessage({ type: 'orbitar:minimize', widgetId: props.widgetId }, '*');
};

// Handle visitor form submission
const handleVisitorSubmit = async (visitorInfo: any) => {
  await initChatWithVisitorInfo(visitorInfo);
};

onMounted(async () => {
  // Listen for external commands from parent window
  window.addEventListener('orbitar:send', handleExternalSendMessage as EventListener);

  // Listen for messages from parent window via postMessage
  const handleParentMessages = (event: MessageEvent) => {
    // You may want to add origin check in production for security
    // if (event.origin !== 'https://your-parent-domain.com') return;
    
    const message = event.data;
    if (message.type === 'orbitar:send' && message.content) {
      sendMessage(message.content);
    }
  };
  window.addEventListener('message', handleParentMessages);
  
  // Load widget configuration from backend
  loading.value = true;
  try {
    await configStore.fetchWidgetConfig(props.widgetId);
    
    // Set widget customization from config
    loginRequired.value = configStore.loginRequired;
    widgetTitle.value = configStore.widgetTitle || 'Orbitar Assistant';
    widgetAvatarUrl.value = configStore.widgetAvatarUrl || '';
    
    // Check for existing user session
    const sessionExists = await sessionStore.checkExistingSession(props.widgetId);
    if (sessionExists && sessionStore.conversationId) {
      authenticated.value = true;
      userId.value = sessionStore.userId;
      
      // Initialize session with existing conversation
      await initExistingSession();
    } else if (!loginRequired.value) {
      // If login not required, we'll wait for visitor info form
      // The form will call initChatWithVisitorInfo when submitted
    }

    // Tell parent frame we're ready
    window.parent.postMessage({ type: 'orbitar:ready', widgetId: props.widgetId }, '*');
  } catch (error) {
    console.error('[Orbitar Chat] Failed to initialize:', error);
  } finally {
    loading.value = false;
  }
  
  // Set up interval to periodically sync messages (for connection issues)
  const syncInterval = setInterval(() => {
    if (authenticated.value && sessionStore.conversationId) {
      // Removed syncMessages call since it's not implemented
      // Instead, we'll rely on socket.io reconnection
    }
  }, 10000); // Every 10 seconds
  
  // Clean up on unmount
  onUnmounted(() => {
    clearInterval(syncInterval);
    window.removeEventListener('orbitar:send', handleExternalSendMessage as EventListener);
    window.removeEventListener('message', handleParentMessages);
    socketService.disconnect();
  });
});

// Watch for socket connection status changes
watchEffect(() => {
  inputDisabled.value = !socketService.isConnected.value || loading.value;
  
  if (socketService.error.value) {
    messageStore.addSystemMessage('Connection error. Trying to reconnect...');
  }
});

// Watch for socket typing status
watchEffect(() => {
  botIsTyping.value = socketService.botIsTyping.value;
});

// Methods
const handleLogin = async (credentials: { username: string, password: string }) => {
  loading.value = true;
  try {
    const success = await sessionStore.login(props.widgetId, credentials);
    if (success) {
      authenticated.value = true;
      userId.value = sessionStore.userId;
      await initExistingSession();
    } else {
      // Handle login failure
      console.error('[Orbitar Chat] Login failed');
    }
  } catch (error) {
    console.error('[Orbitar Chat] Login error:', error);
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
/* Additional component-specific styles can go here */
</style> 